import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import './MembershipStatus.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Territorio del sorteo: misma regla que el backend — la location del usuario
// debe contener el territorio configurado, sin acentos ni mayúsculas.
const normTxt = (s) => String(s || '')
  .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  .toLowerCase().trim();
const enTerritorio = (location, territorio) => {
  const t = normTxt(territorio);
  if (!t) return true; // sorteo sin restricción territorial
  return normTxt(location).includes(t);
};

const DAY = 24 * 60 * 60 * 1000;
const TRIAL_DAYS = 14;

// Deriva el estado de membresía/trial desde el objeto user.
//  phase: 'trial' | 'expired' | 'active_paid' | 'none'
export function getTrialState(user) {
  if (!user) return { phase: 'none', daysLeft: null };
  const expVigente = !user.planExpiresAt || new Date(user.planExpiresAt).getTime() >= Date.now();
  const paid = ['pro', 'premium', 'seller'].includes(user.planActivo) && expVigente;
  const freeUnlocked = user.membresiaGratisDesbloqueada && expVigente;
  if (paid || freeUnlocked) return { phase: 'active_paid', daysLeft: null };
  if (!user.trialStartDate) return { phase: 'none', daysLeft: null };
  const msLeft = new Date(user.trialStartDate).getTime() + TRIAL_DAYS * DAY - Date.now();
  if (msLeft <= 0) return { phase: 'expired', daysLeft: 0 };
  return { phase: 'trial', daysLeft: Math.ceil(msLeft / DAY) };
}

/* ── Banner superior (countdown del trial / expiración) ──────── */
// Troly Pro es gratis para siempre: el countdown del trial y el aviso de
// expiración ya no aplican. Se mantiene el export para no romper Profile.js.
export function MembershipBanner() {
  return null;
}

/* ── Modal: Participar en el sorteo (programa de referidos) ──── */
function FreeMembershipModal({ onClose }) {
  const { user, getReferralStatus, sendReferralInvite } = useAuth();
  const { t } = useTranslation();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  // Flujo "Compartir por correo": revela un campo inline sin salir del modal.
  const [showEmail, setShowEmail] = useState(false);
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState('');
  const [error, setError] = useState('');

  const code = status?.codigoPropio || user?.codigoPropio || '';

  const load = useCallback(async () => {
    setLoading(true);
    try { setStatus(await getReferralStatus()); }
    catch { setStatus(null); }
    finally { setLoading(false); }
  }, [getReferralStatus]);

  useEffect(() => { load(); }, [load]);

  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const submitEmail = async (e) => {
    e?.preventDefault?.();
    const to = email.trim();
    setError('');
    if (!EMAIL_RE.test(to)) { setError(t('profile.free_modal.err_invalid_email')); return; }
    setSending(true);
    try {
      await sendReferralInvite(to);
      setSent(to);
      setEmail('');
      setShowEmail(false);
    } catch (err) {
      setError(err?.response?.data?.message || t('profile.free_modal.err_send_failed'));
    } finally {
      setSending(false);
    }
  };

  const shareWhatsApp = () => {
    const origin = typeof window !== 'undefined' ? window.location.origin : 'https://troly.club';
    const url = `${origin}/registro?code=${encodeURIComponent(code)}`;
    const msg = t('profile.free_modal.wa_message', { code, url });
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank', 'noopener');
  };

  const refDone = status?.referidosCompletos ?? 0;
  const refMeta = status?.referidosMeta ?? 4;
  const piezas = status?.piezasSubidas ?? 0;
  const piezasMeta = status?.piezasMeta ?? 4;
  // Participación activada en la ronda vigente del sorteo.
  const unlocked = status?.sorteoParticipando;
  // Mecánica de dos niveles: números que ya sostiene y cuántos referidos
  // faltan para el siguiente.
  const numeros = status?.sorteoNumeros || [];
  const refParaSiguiente = status?.referidosParaSiguienteNumero ?? refMeta;

  const Bar = ({ value, meta }) => {
    const pct = Math.min(100, Math.round((value / meta) * 100));
    const full = value >= meta;
    return (
      <div className="ms-prog">
        <div className="ms-prog__track"><div className="ms-prog__fill" style={{ width: `${pct}%` }} /></div>
        <span className="ms-prog__num">{value} / {meta} {full && <span className="ms-prog__check">✓</span>}</span>
      </div>
    );
  };

  return createPortal(
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card ms-modal" onClick={(e) => e.stopPropagation()}>
        <h3>{t('profile.free_modal.title')}</h3>

        {unlocked ? (
          <p className="ms-modal__unlocked">
            {t('profile.free_modal.unlocked')}
          </p>
        ) : (
          <p className="ms-modal__intro">
            {t('profile.free_modal.intro')}
          </p>
        )}

        {/* El código va en el paso 2: es el que se comparte para sumar
            números extra. El paso 1 son las piezas, que abren la entrada. */}
        <ol className="ms-steps">
          <li><strong>1.</strong> {t('profile.free_modal.step1')}</li>
          <li><strong>2.</strong> {t('profile.free_modal.step2')}&nbsp;
            <span className="ms-code-inline">{code || '—'}</span>
          </li>
          <li><strong>3.</strong> {t('profile.free_modal.step3')}</li>
        </ol>

        {/* Las piezas van primero: son la condición de entrada. */}
        <div className="ms-progress-block">
          <div className="ms-progress-row">
            <span className="ms-progress-label">{t('profile.free_modal.label_pieces')}</span>
            {loading ? <span className="ms-prog__num">…</span> : <Bar value={piezas} meta={piezasMeta} />}
          </div>
          <div className="ms-progress-row">
            <span className="ms-progress-label">{t('profile.free_modal.label_referrals')}</span>
            {loading ? <span className="ms-prog__num">…</span> : <Bar value={refDone} meta={refMeta} />}
          </div>
        </div>

        {numeros.length > 0 && (
          <div className="ms-numbers">
            <span className="ms-numbers__label">{t('profile.free_modal.your_numbers')}</span>
            <span className="ms-numbers__chips">
              {numeros.map((n) => <span key={n} className="ms-numbers__chip">#{n}</span>)}
            </span>
            <span className="ms-numbers__next">
              {t('profile.free_modal.next_number', { count: refParaSiguiente })}
            </span>
          </div>
        )}

        <p className="ms-modal__foot">
          {t('profile.free_modal.foot')}
          {status?.sorteoTerritorio && (
            <> {t('profile.free_modal.territory_note', { territory: status.sorteoTerritorio })}</>
          )}
        </p>

        {showEmail ? (
          <form className="ms-email-form" onSubmit={submitEmail}>
            <input
              type="email"
              className="ms-email-input"
              placeholder={t('profile.free_modal.email_placeholder')}
              value={email}
              onChange={(e) => { setEmail(e.target.value); setError(''); }}
              autoFocus
              disabled={sending}
            />
            <div className="ms-email-form__row">
              <button
                type="button"
                className="btn-ghost"
                onClick={() => { setShowEmail(false); setError(''); }}
                disabled={sending}
              >
                {t('profile.free_modal.cancel')}
              </button>
              <button type="submit" className="btn-teal" disabled={sending || !code}>
                {sending ? t('profile.free_modal.sending') : t('profile.free_modal.send')}
              </button>
            </div>
          </form>
        ) : (
          <div className="ms-modal__actions">
            <button className="btn-ghost" onClick={() => { setSent(''); setError(''); setShowEmail(true); }} disabled={!code}>
              {t('profile.free_modal.share_email')}
            </button>
            <button className="btn-teal" onClick={shareWhatsApp} disabled={!code}>
              {t('profile.free_modal.share_whatsapp')}
            </button>
          </div>
        )}

        {sent && <p className="ms-email-feedback ms-email-feedback--ok">{t('profile.free_modal.sent_to', { email: sent })}</p>}
        {error && <p className="ms-email-feedback ms-email-feedback--err">{error}</p>}

        <button className="modal-close" onClick={onClose}>✕</button>
      </div>
    </div>,
    document.body
  );
}

/* ── Modal: Invitar a un amigo (invitaciones de un solo uso) ─── */
// Se muestra cuando la dinámica del sorteo está OFF. Consume la cuota
// invitesLeft del usuario (el admin la amplía con «+30 inv» en su panel).
function InviteFriendModal({ onClose }) {
  const { generateInvite, listMyInvites } = useAuth();
  const { t } = useTranslation();
  const [invitesLeft, setInvitesLeft] = useState(null);
  const [loading, setLoading] = useState(true);

  const [showEmail, setShowEmail] = useState(false);
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState('');
  const [lastCode, setLastCode] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    listMyInvites()
      .then((data) => { if (!cancelled) setInvitesLeft(data?.invitesLeft ?? 0); })
      .catch(() => { if (!cancelled) setInvitesLeft(0); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [listMyInvites]);

  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const noneLeft = !loading && (invitesLeft ?? 0) <= 0;

  const submitEmail = async (e) => {
    e?.preventDefault?.();
    const to = email.trim();
    setError('');
    if (!EMAIL_RE.test(to)) { setError(t('profile.invite_modal.err_invalid_email')); return; }
    setSending(true);
    try {
      const { code, emailSent } = await generateInvite(to);
      setLastCode(code || '');
      setSent(emailSent ? to : '');
      if (!emailSent) setError(t('profile.invite_modal.err_send_failed'));
      setInvitesLeft((n) => Math.max(0, (n ?? 1) - 1));
      setEmail('');
      setShowEmail(false);
    } catch (err) {
      setError(err?.response?.data?.message || t('profile.invite_modal.err_send_failed'));
    } finally {
      setSending(false);
    }
  };

  const shareWhatsApp = async () => {
    setError('');
    setSent('');
    setSending(true);
    try {
      const { code } = await generateInvite();
      setLastCode(code || '');
      setInvitesLeft((n) => Math.max(0, (n ?? 1) - 1));
      const origin = typeof window !== 'undefined' ? window.location.origin : 'https://troly.club';
      const url = `${origin}/registro?code=${encodeURIComponent(code)}`;
      const msg = t('profile.invite_modal.wa_message', { code, url });
      window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank', 'noopener');
    } catch (err) {
      setError(err?.response?.data?.message || t('profile.invite_modal.err_generate_failed'));
    } finally {
      setSending(false);
    }
  };

  return createPortal(
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card ms-modal" onClick={(e) => e.stopPropagation()}>
        <h3>{t('profile.invite_modal.title')}</h3>

        <p className="ms-modal__intro">{t('profile.invite_modal.intro')}</p>

        <p className="ms-modal__foot">
          {loading
            ? '…'
            : noneLeft
              ? t('profile.invite_modal.none_left')
              : t('profile.invite_modal.remaining', { count: invitesLeft })}
        </p>

        {showEmail ? (
          <form className="ms-email-form" onSubmit={submitEmail}>
            <input
              type="email"
              className="ms-email-input"
              placeholder={t('profile.invite_modal.email_placeholder')}
              value={email}
              onChange={(e) => { setEmail(e.target.value); setError(''); }}
              autoFocus
              disabled={sending}
            />
            <div className="ms-email-form__row">
              <button
                type="button"
                className="btn-ghost"
                onClick={() => { setShowEmail(false); setError(''); }}
                disabled={sending}
              >
                {t('profile.invite_modal.cancel')}
              </button>
              <button type="submit" className="btn-teal" disabled={sending}>
                {sending ? t('profile.invite_modal.sending') : t('profile.invite_modal.send')}
              </button>
            </div>
          </form>
        ) : (
          <div className="ms-modal__actions">
            <button
              className="btn-ghost"
              onClick={() => { setSent(''); setError(''); setShowEmail(true); }}
              disabled={sending || loading || noneLeft}
            >
              {t('profile.invite_modal.share_email')}
            </button>
            <button
              className="btn-teal"
              onClick={shareWhatsApp}
              disabled={sending || loading || noneLeft}
            >
              {t('profile.invite_modal.share_whatsapp')}
            </button>
          </div>
        )}

        {lastCode && (
          <p className="ms-email-feedback ms-email-feedback--ok">
            {t('profile.invite_modal.code_generated')} <span className="ms-code-inline">{lastCode}</span>
          </p>
        )}
        {sent && <p className="ms-email-feedback ms-email-feedback--ok">{t('profile.invite_modal.sent_to', { email: sent })}</p>}
        {error && <p className="ms-email-feedback ms-email-feedback--err">{error}</p>}

        <button className="modal-close" onClick={onClose}>✕</button>
      </div>
    </div>,
    document.body
  );
}

/* ── Botones de acción para la fila del perfil ───────────────── */
//  Con la dinámica de growth encendida: "Participar en el sorteo" (solo
//  user / user_premium dentro del territorio). Con la dinámica apagada:
//  "Invitar a un amigo" (invitaciones de un solo uso). + "Mejorar plan".
export function MembershipActions() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [modal, setModal] = useState(false);
  const [inviteModal, setInviteModal] = useState(false);
  const [raffleCfg, setRaffleCfg] = useState({ on: true, territorio: '' });

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/api/settings/public`)
      .then(({ data }) => {
        if (cancelled) return;
        setRaffleCfg({
          on: data?.growthRaffleEnabled !== false,
          territorio: data?.sorteoTerritorio ?? '',
        });
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const showRaffle = raffleCfg.on
    && user?.role !== 'seller'
    && enTerritorio(user?.location, raffleCfg.territorio);
  // Con el sorteo apagado, el perfil ofrece el sistema de invitaciones de
  // un solo uso (cuota invitesLeft, ampliable por el admin con «+30 inv»).
  const showInvite = !raffleCfg.on;

  return (
    <>
      {showRaffle && (
        <button className="btn-teal" onClick={() => setModal(true)}>
          {t('profile.enter_raffle')}
        </button>
      )}
      {showInvite && (
        <button className="btn-teal" onClick={() => setInviteModal(true)}>
          {t('profile.invite_friend')}
        </button>
      )}
      <button className="btn-ghost" onClick={() => navigate('/precios')}>
        {t('profile.upgrade_plan')}
      </button>
      {modal && showRaffle && <FreeMembershipModal onClose={() => setModal(false)} />}
      {inviteModal && showInvite && <InviteFriendModal onClose={() => setInviteModal(false)} />}
    </>
  );
}

/* ── Chip público de número de miembro (Bloque 4) ────────────── */
//  "Eres miembro #347 de Troly" — debajo del nombre, antes de stats.
//  El admin puede ocultarlo para todos con el toggle memberChipEnabled.
export function MemberChip() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [enabled, setEnabled] = useState(true);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/api/settings/public`)
      .then(({ data }) => { if (!cancelled) setEnabled(data?.memberChipEnabled !== false); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  if (!enabled || !user?.memberNumber) return null;
  return (
    <span className="ms-member-chip ms-member-chip--public" title={t('profile.member_chip.title')}>
      {t('profile.member_chip.text', { number: user.memberNumber })}
    </span>
  );
}
